import cx from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';

import { UserContext } from '../context/UserContext';
// creates a beautiful scrollbar
import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';

// @mui/material components
import { makeStyles } from '@mui/styles';

// core components
import Footer from 'components/Footer/Footer.js';
import AdminNavbar from 'components/Navbars/AdminNavbar.js';
import Sidebar from 'components/Sidebar/Sidebar.js';
import SweetAlert from 'react-bootstrap-sweetalert';

import clientRoutes from '@src/clientRoutes';
import routes from '@src/routes.js';

import styles from 'assets/jss/material-dashboard-pro-react/layouts/adminStyle.js';

import logo from 'assets/img/InStore/InStore-Inverted-Logo.png';

import {
  useLazyGetClientsQuery,
  useLazyGetClientTenantsQuery,
  useLazyGetTenantClientQuery,
} from 'api/client.api';
import {
  useLazyGetDeviceDictionaryQuery,
  useLazyGetLocationsQuery,
} from 'api/location.api';
import {
  useLazyGetParameterDictionaryQuery,
  useLazyGetParameterListsQuery,
  useLazyGetTemplateDictionaryQuery,
  useLazyGetTemplateSetsQuery,
} from 'api/parameter.api';
import {
  useLazyGetBaseReferenceListsQuery,
  useLazyGetReferenceListsQuery,
} from 'api/reference-list.api';
import {
  useLazyGetBaseRolesQuery,
  useLazyGetPermissionsQuery,
  useLazyGetRolesQuery,
} from 'api/role.api';
import { useLazyGetSessionQuery } from 'api/session.api';
import {
  useLazyGetTenantQuery,
  useLazyGetTenantStatsQuery,
} from 'api/tenant.api';
import { useLazyGetCurrentUserQuery } from 'api/user.api';
import { setClientTenants } from 'features/client/clientSlice';
import { retrieveNotifications } from 'features/notification/notificationSlice';
import dashboardDates from 'views/Dashboard/dashboardDates.const';
import {
  selectClient,
  selectClientTenant,
  selectImpersonationMode,
  selectSessionStatus,
  setSessionClient,
  setSessionData,
  setTenantSessionData,
  setToken,
  setSessionTenant,
} from '../features/session/sessionSlice';
import { selectNtAdministrator, setUser } from '../features/user/userSlice';

const useStyles = makeStyles(styles);

export default function Dashboard(props) {
  const dispatch = useDispatch();

  const [triggerGetSession] = useLazyGetSessionQuery();
  const [triggerGetTenant] = useLazyGetTenantQuery();
  const [triggerGetTenantStats] = useLazyGetTenantStatsQuery();
  const [triggerGetParameterDictionary] = useLazyGetParameterDictionaryQuery();
  const [triggerGetTemplateDictionary] = useLazyGetTemplateDictionaryQuery();
  const [triggerGetParameterLists] = useLazyGetParameterListsQuery();
  const [triggerGetTemplateSets] = useLazyGetTemplateSetsQuery();
  const [triggerGetTenantClient] = useLazyGetTenantClientQuery();
  const [triggerGetClients] = useLazyGetClientsQuery();
  const [triggerGetClientTenants] = useLazyGetClientTenantsQuery();
  const [triggerGetCurrentUser] = useLazyGetCurrentUserQuery();
  const [triggerGetPermissions] = useLazyGetPermissionsQuery();
  const [triggerGetBaseRoles] = useLazyGetBaseRolesQuery();
  const [triggerGetRoles] = useLazyGetRolesQuery();
  const [triggerGetLocations] = useLazyGetLocationsQuery();
  const [triggerGetDeviceDictionary] = useLazyGetDeviceDictionaryQuery();
  const [triggerGetBaseReferenceLists] = useLazyGetBaseReferenceListsQuery();
  const [triggerGetReferenceLists] = useLazyGetReferenceListsQuery();

  const ntAdministrator = useSelector(selectNtAdministrator);
  const sessionStatus = useSelector(selectSessionStatus);
  const impersonationMode = useSelector(selectImpersonationMode);
  const client = useSelector(selectClient);
  const tenant = useSelector(selectClientTenant);

  const [userContext, setUserContext] = useContext(UserContext);

  const initializeAdministrativeSession = async (token) => {
    try {
      const sessionData = await triggerGetSession({ token }).unwrap();
      dispatch(setToken(sessionData?.token));

      await Promise.allSettled([
        triggerGetPermissions().unwrap(),
        triggerGetBaseRoles().unwrap(),
        triggerGetClients().unwrap(),
        triggerGetParameterDictionary().unwrap(),
        triggerGetTemplateDictionary().unwrap(),
        triggerGetBaseReferenceLists().unwrap(),
        triggerGetDeviceDictionary().unwrap(),
      ]);

      dispatch(setSessionData(sessionData));
    } catch (error) {
      dispatch(setSessionData({ token }));
    }
  };

  const initializeTenantSession = async (token) => {
    try {
      const sessionData = await triggerGetSession({ token }).unwrap();
      dispatch(setToken(sessionData?.token));
      const period = dashboardDates[0].replace(/\s+/g, '').toLowerCase();
      
      const tenantClient = await triggerGetTenantClient().unwrap();
      const clientTenants = await triggerGetClientTenants(
        tenantClient._id
      ).unwrap();
      dispatch(setClientTenants(clientTenants));
      dispatch(setSessionClient(tenantClient));

      const tenantData = await triggerGetTenant().unwrap();
      dispatch(setSessionTenant(tenantData));

      await Promise.allSettled([
        triggerGetTenantStats({ period }).unwrap(),
        triggerGetPermissions().unwrap(),
        triggerGetBaseRoles().unwrap(),
        triggerGetParameterDictionary().unwrap(),
        triggerGetTemplateDictionary().unwrap(),
        triggerGetBaseReferenceLists().unwrap(),
        triggerGetDeviceDictionary().unwrap(),
        triggerGetRoles().unwrap(),
        triggerGetReferenceLists().unwrap(),
        triggerGetLocations({ limit: 20 }).unwrap(),
        triggerGetParameterLists().unwrap(),
        triggerGetTemplateSets().unwrap(),
        dispatch(retrieveNotifications()),
      ]);
      
      dispatch(setTenantSessionData(sessionData));
    } catch (error) {
      dispatch(setTenantSessionData({ token }));
    }
  };

  const fetchUserDetails = useCallback(() => {
    triggerGetCurrentUser(userContext.token)
      .unwrap()
      .then(async (response) => {
        if (response) {
          setUserContext((oldValues) => ({ ...oldValues, details: response }));
          dispatch(setUser(response));
          try {
            if (response.nt_administrator === true) {
              await initializeAdministrativeSession(userContext.token);
            } else {
              await initializeTenantSession(userContext.token);
            }
          } catch (error) {
            dispatch(setSessionData({ token: userContext.token }));
          }
        } else {
          if (response?.status === 401) {
            // Edge case: when the token has expired.
            // This could happen if the refreshToken calls have failed due to network error or
            // User has had the tab open from previous day and tries to click on the Fetch button
            window.location.reload();
            return;
          }
        }
      })
      .catch((error) => {
        if (error?.status === 401) {
          window.location.reload();
        } else {
          setTimeout(() => {
            setInitializing(false);
          }, 5000);
        }
      });
  }, [setUserContext, userContext.token, dispatch]);

  useEffect(() => {
    // fetch only when user details are not present
    if (!userContext.details) {
      fetchUserDetails();
    }
  }, [userContext.details, fetchUserDetails]);

  const [initializing, setInitializing] = useState(true);
  useEffect(() => {
    if (sessionStatus === 'active') {
      setInitializing(false);
    }
  }, [sessionStatus]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (initializing) {
        setInitializing(false);
      }
    }, 30000);

    return () => clearTimeout(timeout);
  }, [initializing]);

  const initializingAlert = () => {
    return (
      <SweetAlert
        style={{ display: 'block', marginTop: '-100px' }}
        title='Initializing!'
        showConfirm={false}
        onConfirm={() => {}}
      >
        Please wait...
      </SweetAlert>
    );
  };

  const { ...rest } = props;
  // states and functions
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [miniActive, setMiniActive] = React.useState(false);
  const color = 'blue';
  const bgColor = 'black';
  // styles
  const classes = useStyles();
  const mainPanelClasses =
    classes.mainPanel +
    ' ' +
    cx({
      [classes.mainPanelSidebarMini]: miniActive,
      [classes.mainPanelWithPerfectScrollbar]:
        navigator.userAgent.indexOf('Win') > -1,
    });
  // ref for main panel div
  const mainPanel = React.createRef();
  const ps = React.useRef(null);

  // effect instead of componentDidMount, componentDidUpdate and componentWillUnmount
  React.useEffect(() => {
    if (navigator.userAgent.indexOf('Win') > -1) {
      ps.current = new PerfectScrollbar(mainPanel.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
      document.body.style.overflow = 'hidden';
    }
    window.addEventListener('resize', resizeFunction);

    // Specify how to clean up after this effect:
    return function cleanup() {
      if (navigator.userAgent.indexOf('Win') > -1) {
        if (ps.current) {
          ps.current.destroy();
          ps.current = null;
        }
      }
      window.removeEventListener('resize', resizeFunction);
    };
  }, []);

  React.useEffect(() => {
    if (navigator.userAgent.indexOf('Win') > -1 && ps.current) {
      mainPanel.current.scrollTop = 0;
      ps.current.update();
    }
  }, [props.location.pathname]);
  // functions for changeing the states from components
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const getActiveRoute = (routes) => {
    let activeRoute = 'Default Brand Text';
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].views);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };
  const getRoutes = (routes) => {
    const theRoutes = routes.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      //
      //  check if validation key is true
      //    create validation array in session with validation key and boolean status
      //    add getter and wrap with local function to lookup the key to get the status
      //
      if (prop.layout === '/admin') {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
    return theRoutes;
  };
  const sidebarMinimize = () => {
    setMiniActive(!miniActive);
  };
  const resizeFunction = () => {
    if (window.innerWidth >= 960) {
      setMobileOpen(false);
    }
  };

  return (
    <div className={classes.wrapper}>
      {initializing ? initializingAlert() : null}
      <Sidebar
        routes={
          ntAdministrator
            ? impersonationMode
              ? clientRoutes
              : routes
            : clientRoutes
        }
        logoText={'InStore Center'}
        logo={logo}
        handleDrawerToggle={handleDrawerToggle}
        open={mobileOpen}
        color={color}
        bgColor={bgColor}
        miniActive={miniActive}
        user={
          ntAdministrator === false
            ? client !== null
              ? client.name
              : ''
            : impersonationMode && client !== null
            ? client.name
            : ''
        }
        client={client}
        tenantName={tenant ? tenant.tenant_name : ''}
        {...rest}
      />
      <div className={mainPanelClasses} ref={mainPanel}>
        <AdminNavbar
          sidebarMinimize={sidebarMinimize.bind(this)}
          miniActive={miniActive}
          brandText={getActiveRoute(
            ntAdministrator
              ? impersonationMode
                ? clientRoutes
                : routes
              : clientRoutes
          )}
          handleDrawerToggle={handleDrawerToggle}
          {...rest}
        />
        <div className={classes.content}>
          <div className={classes.container}>
            <Switch>
              {sessionStatus === 'active'
                ? getRoutes(
                    ntAdministrator
                      ? impersonationMode
                        ? clientRoutes
                        : routes
                      : clientRoutes
                  )
                : null}
              <Redirect from='/admin' to='/admin/dashboard' />
            </Switch>
          </div>
        </div>
        <Footer fluid />
      </div>
    </div>
  );
}
